import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { LivePopulation } from "../components/LivePopulation";

const usd = (n) =>
  n >= 1e9 ? `$${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : `$${Math.round(n).toLocaleString()}`;
const kes = (n) =>
  n >= 1e12 ? `KSh ${(n / 1e12).toFixed(2)}tn` : n >= 1e9 ? `KSh ${(n / 1e9).toFixed(1)}bn` : `KSh ${Math.round(n).toLocaleString()}`;
const int = (n) => (n || n === 0 ? Number(n).toLocaleString() : "—");

const Card = ({ label, value, sub, testId }) => (
  <div className="bg-[#0b0b0b] p-6" data-testid={testId}>
    <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
    <p className="mt-4 font-display text-2xl font-bold leading-none text-white md:text-3xl">{value}</p>
    {sub ? <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{sub}</p> : null}
  </div>
);

export default function ValueCase() {
  const [d, setD] = useState(null);
  useSeo({
    title: "The Value Case — KickStartKenyaFootball",
    description:
      "Reconciling the proposed football facilities and their estimated costs against county population and demographics, existing and needed infrastructure, GDP and GDP PPP in KES and USD, diaspora remittances and betting spend per county, and the value of grassroots and professional football.",
  });

  useEffect(() => {
    api.get("/value-case").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d)
    return (
      <div className="flex min-h-[70svh] items-center justify-center bg-black" data-testid="value-loading">
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-400">Loading</p>
      </div>
    );

  const t = d.totals;

  return (
    <div className="bg-black pb-24 pt-16 md:pt-24" data-testid="value-page">
      <StageBar stage="invest" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Stage three — paying for it</p>
          <h1 className="display-1 mt-5 max-w-4xl">What It Costs, What It Returns</h1>
          <p className="body-copy mt-6 max-w-3xl">
            One page reconciling everything: the facilities we propose and their estimated cost, the county population
            and demographics behind them — kept current with the live national estimate — what infrastructure already
            exists or is missing, Kenya&rsquo;s GDP and GDP at purchasing power parity in both shillings and dollars,
            remittances and betting spend allocated per county, and what the built programme returns: jobs, coaching and
            refereeing qualifications, organised competition, healthcare savings, football tourism, VAT and payroll tax,
            and a GDP line for a sector currently recorded at close to nothing. Reference year{" "}
            {d.reference_year} ({d.reference_status}).
          </p>
        </Reveal>

        <div className="mt-14 flex flex-wrap gap-4">
          <a href={`${API}/research-pack.pdf`} className="btn-primary" data-testid="value-prospectus-download">
            <Download size={14} /> Download the one-page research pack
          </a>
        </div>

        <div className="mt-12">
          <LivePopulation pop={d.population} />
        </div>

        <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="value-headlines">
          <Card label="Capital cost, all facilities" value={usd(t.capital_usd)} sub={kes(t.capital_kes)} testId="value-capital" />
          <Card label="Modelled annual revenue" value={usd(t.annual_revenue_usd)} sub={`${kes(t.annual_revenue_kes)} · surplus matches the build cost in ${t.payback_years} yrs`} testId="value-revenue" />
          <Card label="Grassroots social value a year" value={usd(t.grassroots_value_usd)} sub={`${int(t.players_served)} players served`} testId="value-grassroots" />
          <Card label="Professional value upside" value={usd(t.professional_upside_usd)} sub="Career value, against today's $88.4m" testId="value-pro" />
          <Card label="GDP" value={usd(t.gdp_usd)} sub={kes(t.gdp_kes)} testId="value-gdp" />
          <Card label="GDP (PPP)" value={usd(t.gdp_ppp_usd)} sub={kes(t.gdp_ppp_kes)} testId="value-gdp-ppp" />
          <Card label="Diaspora remittances" value={usd(t.remittances_usd)} sub={`${kes(t.remittances_kes)} · plan is ${t.capital_vs_remittances_pct}% of one year`} testId="value-remittances" />
          <Card label="Betting staked on M-Pesa" value={usd(t.betting_usd)} sub={`${kes(t.betting_kes)} · plan is ${t.capital_vs_betting_pct}% of one year`} testId="value-betting" />
        </div>

        <div className="mt-20 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2" data-testid="value-infrastructure">
          {d.infrastructure.map(([label, body], i) => (
            <div key={label} className="bg-[#0b0b0b] p-8">
              <span className="mono text-xs text-maasai">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-5 font-display text-lg uppercase tracking-tight text-white md:text-xl">{label}</h3>
              <p className="body-copy mt-4">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 grid gap-8 lg:grid-cols-2">
          <div className="border-l-2 border-ochre bg-[#0b0b0b] p-8" data-testid="value-grassroots-benchmark">
            <p className="overline text-ochre">Grassroots benchmark</p>
            <h3 className="display-3 mt-4">England &amp; Wales as the yardstick</h3>
            <p className="body-copy mt-5">{d.grassroots_benchmark.note}</p>
            <div className="mono mt-6 grid grid-cols-2 gap-4 text-sm text-zinc-300">
              <span>{int(d.grassroots_benchmark.pitches)} pitches</span>
              <span>{d.grassroots_benchmark.players_m}m players</span>
              <span>£{d.grassroots_benchmark.social_value_gbp_bn}bn a year</span>
              <span>${d.grassroots_benchmark.social_value_per_player_usd} per player</span>
            </div>
          </div>
          <div className="border-l-2 border-maasai bg-[#0b0b0b] p-8" data-testid="value-professional-model">
            <p className="overline text-maasai">Professional model</p>
            <h3 className="display-3 mt-4">What better facilities are worth</h3>
            <p className="body-copy mt-5">{d.professional_model.note}</p>
            <p className="body-copy mt-4">{d.youth.note}</p>
            <div className="mono mt-6 grid grid-cols-2 gap-4 text-sm text-zinc-300">
              <span>Median age {d.youth.kenya_median_age} (Kenya)</span>
              <span>Median age {d.youth.east_africa_median_age} (East Africa)</span>
              <span>{d.professional_model.current_pros} pros today</span>
              <span>{d.professional_model.target_pros} pros targeted</span>
            </div>
          </div>
        </div>

        <div className="mt-20" data-testid="value-counties">
          <p className="eyebrow">County by county</p>
          <h2 className="display-2 mt-4">Where the money and the people are</h2>
          <p className="body-copy mt-5 max-w-3xl">
            Remittances and betting spend are allocated by each county&rsquo;s share of national population — modelled
            allocations, not measured county data. Population today is the 2019 census figure scaled by the live
            Worldometer national estimate. Scroll the list.
          </p>
          <div
            className="mt-8 max-h-[60vh] overflow-auto border border-white/10 [&::-webkit-scrollbar-thumb]:bg-maasai [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar]:w-2"
            style={{ scrollbarColor: "#E32118 rgba(255,255,255,0.05)", scrollbarWidth: "thin" }}
            data-testid="value-county-scroll"
          >
            <table className="w-full min-w-[980px] text-left">
              <thead className="sticky top-0 bg-[#0b0b0b]">
                <tr className="border-b border-white/15">
                  {["County", "Rank", "Population 2019", "Population today", "Women/man", "Youth 15–24", "In secondary", "GDP", "GDP PPP", "Remittances", "Betting", "Priority 18"].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {d.counties.map((c, i) => (
                  <tr key={c.county} className="border-b border-white/5" data-testid={`value-county-${i}`}>
                    <td className="px-4 py-3 font-display text-sm uppercase tracking-tight text-white">{c.county}</td>
                    <td className="mono px-4 py-3 text-sm text-maasai">{c.priority_rank ? `#${c.priority_rank}` : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{int(c.population)}</td>
                    <td className="mono px-4 py-3 text-sm text-white" data-testid={`value-county-now-${i}`}>{int(c.population_now)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-400">{c.women_per_man?.toFixed(3)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{c.youth_population ? int(c.youth_population) : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{c.secondary_enrolment ? int(c.secondary_enrolment) : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-400">{c.gdp_usd_bn ? `$${c.gdp_usd_bn}bn` : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-400">{c.gdp_ppp_usd_bn ? `$${c.gdp_ppp_usd_bn}bn` : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{usd(c.remittances_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-maasai">{kes(c.betting_kes)}</td>
                    <td className="px-4 py-3 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                      {c.priority ? `Yes · #${c.priority_rank}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-16 border border-white/10 bg-[#0b0b0b] p-8" data-testid="value-assumptions">
          <p className="overline">Assumptions and limits</p>
          <ul className="mt-5 space-y-3">
            {d.assumptions.map((a) => (
              <li key={a} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-ochre" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/apply" className="btn-primary" data-testid="value-proposal-cta">
            Challenge these numbers <ArrowRight size={14} />
          </Link>
          <Link to="/sources" className="btn-ghost" data-testid="value-sources-cta">
            Sources and Method
          </Link>
          <Link to="/facilities" className="btn-ghost" data-testid="value-ecosystem-cta">
            What each facility costs to build
          </Link>
          <Link to="/plans" className="btn-ghost">
            The recommended steps
          </Link>
          <Link to="/counties" className="btn-ghost">
            County map
          </Link>
        </div>
      </div>
      <StageClose stage="invest" />
    </div>
  );
}
